"use client";

import { useTranslations } from "next-intl";
import { useClientSnapshot } from "@/lib/hooks/use-client-snapshot";
import { lastSignInFrom, type SignInMethod } from "@/lib/last-signin";

/**
 * The sign-in method this browser last completed (lib/last-signin.ts), or null.
 * Read after hydration only, so the prerendered auth pages ship no tag and stay
 * static; the tag appears a moment later, absolutely positioned, so nothing
 * moves when it does.
 */
export function useLastSignIn(): SignInMethod | null {
  return useClientSnapshot(() => lastSignInFrom(document.cookie), null);
}

/**
 * "Last used" (owner, 2026-10-04: variant A of
 * `design/last-used-signin-mockup.html`). `corner` straddles the top-right
 * edge of a button, which must be `relative`; `onGold` is the navy version for
 * the gold Sign in button, where a white pill would read as part of the button;
 * `inline` follows a text link.
 */
export default function LastUsedTag({ placement }: { placement: "corner" | "onGold" | "inline" }) {
  const t = useTranslations("auth");
  const base = "inline-flex items-center whitespace-nowrap rounded-full border px-8 py-4 text-2xs font-semibold normal-case leading-none tracking-normal";
  const where =
    placement === "inline"
      ? "ml-8 align-[2px] border-navy/35 bg-white text-navy"
      : `pointer-events-none absolute right-13 top-0 -translate-y-1/2 ${placement === "onGold" ? "border-navy bg-navy text-white" : "border-navy/35 bg-white text-navy"}`;
  return <span className={`${base} ${where}`}>{t("lastUsed")}</span>;
}
